import { createTheme } from '@mui/material/styles';
import type {} from '@mui/x-date-pickers/themeAugmentation';

import './fonts';

export const grey = {
	50: '#fafafa',
	100: '#f5f5f5',
	200: '#eeeeee',
	300: '#e0e0e0',
	400: '#bdbdbd',
	500: '#9e9e9e',
	600: '#757575',
	700: '#616161',
	800: '#424242',
	900: '#212121',
	A100: '#f5f5f5',
	A200: '#eeeeee',
	A400: '#bdbdbd',
	A700: '#616161'
};

export const theme = createTheme({
	palette: {
		common: {
			black: '#000',
			white: '#fff'
		},
		primary: {
			main: '#1565c0',
			light: '#8fcaf8',
			lighter: '#E3F2FD',
			hover: '#f6fbff',
			contrastText: '#fff'
		},
		secondary: {
			main: '#9c27b0',
			light: '#ba68c8',
			dark: '#7b1fa2',
			contrastText: '#fff'
		},
		error: {
			main: '#d32f2f',
			light: '#ef5350',
			dark: '#c62828',
			contrastText: '#fff'
		},
		warning: {
			main: '#ed6c02',
			light: '#ff9800',
			dark: '#e65100',
			contrastText: '#fff'
		},
		info: {
			main: '#0288d1',
			light: '#03a9f4',
			dark: '#01579b',
			contrastText: '#fff'
		},
		success: {
			main: '#2e7d32',
			light: '#4caf50',
			dark: '#1b5e20',
			contrastText: '#fff'
		},
		text: {
			primary: '#121212',
			secondary: '#717171',
			disabled: '#9e9e9e',
			fadedPrimary: '#313131'
		},
		background: {
			paper: '#fff',
			default: '#f5f5f5'
		},
		action: {
			active: 'rgba(0, 0, 0, 0.54)',
			hover: 'rgba(18, 18, 18, 0.3)',
			hoverOpacity: 0.04,
			selected: 'rgba(0, 0, 0, 0.08)',
			selectedOpacity: 0.08,
			disabled: 'rgba(0, 0, 0, 0.26)',
			disabledBackground: 'rgba(0, 0, 0, 0.12)',
			disabledOpacity: 0.38,
			focus: 'rgba(0, 0, 0, 0.12)',
			focusOpacity: 0.12,
			activatedOpacity: 0.12
		},
		grey,
		divider: '#e2e2e2',
		orange: {
			main: '#ff9800',
			light: '#ffac32',
			contrastText: '#fff'
		},
		highlight: {
			main: '#ff990069',
			light: '#ffba5369',
			contrastText: '#000000'
		}
	},
	components: {
		MuiButton: {
			defaultProps: {
				disableElevation: true // It is a product decision to not have any elevation (shadows) on buttons
			},
			styleOverrides: {
				root: {
					variants: [
						{
							props: { variant: 'outlined' },
							style: {
								borderColor: 'var(--mui-palette-primary-light)',
								'&:hover': {
									borderColor: 'var(--mui-palette-primary-main)',
									backgroundColor: 'var(--mui-palette-primary-hover)'
								}
							}
						}
					]
				}
			}
		},
		MuiAlert: {
			styleOverrides: {
				root: {
					variants: [
						{
							props: { variant: 'standard', color: 'info' },
							style: {
								backgroundColor: '#2195f409',
								border: '1px solid #90CAF9'
							}
						}
					]
				}
			}
		},
		MuiPagination: {
			styleOverrides: {
				ul: {
					flexWrap: 'nowrap'
				}
			}
		},
		MuiLink: {
			styleOverrides: {
				root: {
					fontWeight: '700',
					'& .MuiTypography-root': {
						fontWeight: '700'
					}
				}
			}
		},
		MuiTextField: {
			styleOverrides: {
				root: {
					'& .MuiInputBase-input::placeholder': {
						color: 'var(--mui-palette-text-secondary)',
						opacity: 1
					}
				}
			}
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					'& .MuiOutlinedInput-notchedOutline': {
						borderColor: 'var(--mui-palette-divider)',
						borderWidth: 1
					},
					'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
						borderColor: 'var(--mui-palette-primary-main)',
						borderWidth: 1
					}
				},
				input: {
					'&:-webkit-autofill': {
						WebkitBoxShadow:
							'0 0 0 1000px var(--mui-palette-background-paper) inset',
						WebkitTextFillColor: 'currentColor',
						borderRadius: 'inherit',
						fontFamily: 'inherit',
						fontSize: 'inherit !important',
						caretColor: 'inherit'
					}
				}
			}
		},
		MuiPickersOutlinedInput: {
			styleOverrides: {
				root: {
					backgroundColor: 'var(--mui-palette-background-paper)',
					width: '100%',
					height: '48px !important',

					'&.MuiPickersInputBase-inputSizeSmall': {
						height: '40px !important'
					},

					'& .MuiPickersOutlinedInput-input': {
						color: 'var(--mui-palette-text-primary)',
						WebkitTextFillColor: 'currentColor'
					},

					'& .MuiInputAdornment-root': {
						marginRight: '2px'
					},

					'& .MuiPickersOutlinedInput-notchedOutline': {
						borderColor: 'var(--mui-palette-divider)',
						borderWidth: 1
					},

					'&:hover .MuiPickersOutlinedInput-notchedOutline': {
						borderColor: 'var(--mui-palette-action-hover)',
						borderWidth: 1
					},

					'&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
						borderColor: 'var(--mui-palette-primary-main)',
						borderWidth: 1
					},

					'&.Mui-error .MuiPickersOutlinedInput-notchedOutline': {
						borderColor: 'var(--mui-palette-error-main)',
						borderWidth: 1
					}
				}
			}
		},
		MuiMenuItem: {
			styleOverrides: {
				root: {
					'&.Mui-selected': {
						backgroundColor: 'var(--mui-palette-grey-50)',
						fontWeight: 700
					},
					'&:hover, &.Mui-selected:hover, &.Mui-focusVisible': {
						backgroundColor: 'var(--mui-palette-primary-lighter)'
					}
				}
			}
		},
		MuiAutocomplete: {
			styleOverrides: {
				paper: {
					marginTop: '8px',
					borderRadius: 4,
					boxShadow: '0px 4px 6px 1px rgb(0 0 0 / 0.15)'
				},
				listbox: {
					padding: 0,
					maxHeight: 250
				},
				option: {
					minHeight: 36,
					paddingLeft: '12px',
					paddingRight: '12px',
					'&[aria-selected="true"]': {
						backgroundColor: 'var(--mui-palette-grey-50)',
						fontWeight: 700
					},
					'&.Mui-focused, &.Mui-focused[aria-selected="true"]': {
						backgroundColor: 'var(--mui-palette-primary-lighter)'
					}
				}
			}
		}
	},
	typography: {
		fontFamily: [
			'Lato',
			'-apple-system',
			'BlinkMacSystemFont',
			'"Segoe UI"',
			'"Helvetica Neue"',
			'Arial',
			'sans-serif',
			'"Apple Color Emoji"',
			'"Segoe UI Emoji"',
			'"Segoe UI Symbol"'
		].join(',')
	},
	cssVariables: true,
	breakpoints: {
		values: {
			xs: 0,
			sm: 600,
			md: 900,
			mdlg: 1000,
			lg: 1200,
			xl: 1536
		}
	},
	customShadows: {
		scrollDown:
			'0px 4px 10px -2px rgba(0, 0, 0, 0.05), 0px 7px 18px -4px rgba(0, 0, 0, 0.07)',
		scrollUp:
			'0px -4px 10px -2px rgba(0, 0, 0, 0.05), 0px -7px 18px -4px rgba(0, 0, 0, 0.07)',
		floatingPanel: '0 4px 20px rgba(0, 0, 0, 0.18)'
	}
});
